// Website monitors (task 40, PLAN §14 M4): scheduled crawls of web pages with Browser Rendering. A page whose text
// changed since the last check is stored as a new document (source channel `scraper`) and goes through the same
// IngestWorkflow as an upload; the earlier snapshot stays, linked as the version it replaces.
import type { StageDef } from './progress';
import type { ExtractionDepth, ReviewMode, Sensitivity } from './uploads';

export const SCRAPER_SCHEDULES = ['hourly', 'every_6_hours', 'daily', 'weekly', 'manual'] as const;
export type ScraperSchedule = (typeof SCRAPER_SCHEDULES)[number];

const HOUR = 60 * 60 * 1000;
/** Time between scheduled checks; `manual` monitors only run when someone presses "Check now". */
export const SCHEDULE_INTERVAL_MS: Record<ScraperSchedule, number | null> = {
  hourly: HOUR,
  every_6_hours: 6 * HOUR,
  daily: 24 * HOUR,
  weekly: 7 * 24 * HOUR,
  manual: null,
};
export const SCHEDULE_LABELS: Record<ScraperSchedule, string> = {
  hourly: 'Every hour',
  every_6_hours: 'Every 6 hours',
  daily: 'Every day',
  weekly: 'Every week',
  manual: 'Only when checked by hand',
};

/** `page`: the address only. `section`: also the pages it links to under the same path, up to `maxPages`. */
export const SCRAPER_SCOPES = ['page', 'section'] as const;
export type ScraperScope = (typeof SCRAPER_SCOPES)[number];

/** `browser`: Cloudflare Browser Rendering (runs the page's scripts). `static`: the HTML as the server sends it. */
export const SCRAPER_RENDERINGS = ['browser', 'static'] as const;
export type ScraperRendering = (typeof SCRAPER_RENDERINGS)[number];

export const SCRAPER_LIMITS = {
  maxPages: { min: 1, max: 50 },
  /** Pages larger than this are skipped (the HTML as received). */
  pageBytes: 5 * 1024 * 1024,
  selectorChars: 300,
  /** Consecutive failed runs after which a monitor pauses itself (and tells its owner). */
  pauseAfterFailures: 5,
} as const;

export const SCRAPER_RUN_STAGES: StageDef[] = [
  { key: 'checking', label: 'Checking pages', stallAfterMs: 90_000 },
  { key: 'storing', label: 'Storing changes', stallAfterMs: 60_000 },
  { key: 'done', label: 'Done', stallAfterMs: Number.POSITIVE_INFINITY },
];

export type ScraperStatus = 'active' | 'paused' | 'failing';
export type ScraperPageOutcome = 'new' | 'changed' | 'unchanged' | 'failed' | 'blocked';
export type ScraperRunStatus = 'running' | 'ok' | 'partial' | 'failed';

export interface ScraperInput {
  name?: string;
  url?: string;
  scope?: ScraperScope;
  maxPages?: number;
  schedule?: ScraperSchedule;
  rendering?: ScraperRendering;
  /** CSS selector: only the text inside it is compared and kept (e.g. `main`, `#content`). Empty: the whole page. */
  focusSelector?: string | null;
  /** CSS selectors (comma-separated) whose text is ignored, e.g. a clock, a visitor counter or an ad slot. */
  ignoreSelectors?: string | null;
  sensitivity?: Sensitivity;
  reviewMode?: ReviewMode;
  extractionDepth?: ExtractionDepth | null;
}

export interface ScraperSummary {
  id: string;
  name: string;
  url: string;
  scope: ScraperScope;
  maxPages: number;
  schedule: ScraperSchedule;
  rendering: ScraperRendering;
  focusSelector: string | null;
  ignoreSelectors: string | null;
  sensitivity: Sensitivity;
  reviewMode: ReviewMode;
  extractionDepth: ExtractionDepth | null;
  status: ScraperStatus;
  enabled: boolean;
  owner: { id: string; name: string | null; email: string };
  nextRunAt: number | null;
  lastRunAt: number | null;
  lastStatus: ScraperRunStatus | null;
  lastError: string | null;
  consecutiveFailures: number;
  running: boolean;
  pages: number;
  changes: number;
  createdAt: number;
}

export interface ScraperRunSummary {
  id: string;
  trigger: 'schedule' | 'manual';
  rendering: ScraperRendering;
  status: ScraperRunStatus;
  startedAt: number;
  finishedAt: number | null;
  pagesChecked: number;
  pagesNew: number;
  pagesChanged: number;
  pagesUnchanged: number;
  pagesFailed: number;
  error: string | null;
}

export interface ScraperPageSummary {
  url: string;
  title: string | null;
  outcome: ScraperPageOutcome;
  error: string | null;
  lastCheckedAt: number | null;
  lastChangedAt: number | null;
  documentId: string | null;
}

export interface ScraperChangeSummary {
  id: string;
  url: string;
  title: string | null;
  kind: 'new' | 'changed';
  documentId: string;
  previousDocumentId: string | null;
  added: number;
  removed: number;
  excerpt: TextDiffExcerpt;
  detectedAt: number;
}

export interface ScraperDetail {
  scraper: ScraperSummary;
  runs: ScraperRunSummary[];
  pages: ScraperPageSummary[];
  changes: ScraperChangeSummary[];
}

export interface ScraperSettings {
  /** The kill switch (PLAN §10 feature flags): off stops every scheduled and manual check without a deploy. */
  enabled: boolean;
  /** Browser Rendering is bound in this environment (otherwise monitors fetch pages statically). */
  browserAvailable: boolean;
}

// ── Addresses ────────────────────────────────────────────────────────────────────────────────────────────

const PRIVATE_V4 = [
  /^0\./,
  /^10\./,
  /^127\./,
  /^169\.254\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^192\.168\./,
  /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./,
];

/**
 * Whether an address may be monitored: http(s), no user name or password, and not a private, local or
 * link-local host. `allowLocal` (local development only) lets e2e tests watch a page the dev server serves.
 */
export function checkMonitorUrl(raw: string, allowLocal = false): { ok: true; url: string } | { ok: false; message: string } {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return { ok: false, message: 'Enter a full web address, starting with https://.' };
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return { ok: false, message: 'Only http and https addresses can be monitored.' };
  if (u.username || u.password) return { ok: false, message: 'Leave the user name and password out of the address.' };
  if (raw.length > 2000) return { ok: false, message: 'Keep the address under 2,000 characters.' };
  const host = u.hostname.toLowerCase().replace(/^\[|\]$/g, '');
  const local =
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host.endsWith('.local') ||
    host.endsWith('.internal') ||
    host.endsWith('.home.arpa') ||
    !host.includes('.') ||
    PRIVATE_V4.some(r => r.test(host)) ||
    host === '::1' ||
    host === '::' ||
    /^f[cd][0-9a-f]{2}:/.test(host) ||
    /^fe80:/.test(host) ||
    /^::ffff:/.test(host);
  if (local && !(allowLocal && (host === 'localhost' || host === '127.0.0.1')))
    return { ok: false, message: 'Only public websites can be monitored, not local or private network addresses.' };
  u.hash = '';
  return { ok: true, url: u.toString() };
}

/** Links a `section` monitor follows: same origin, under the start address's folder, not an obvious file download. */
export function inSection(start: string, candidate: string): boolean {
  let a: URL;
  let b: URL;
  try {
    a = new URL(start);
    b = new URL(candidate);
  } catch {
    return false;
  }
  if (a.origin !== b.origin) return false;
  const folder = a.pathname.slice(0, a.pathname.lastIndexOf('/') + 1);
  if (!b.pathname.startsWith(folder)) return false;
  return !/\.(pdf|docx?|xlsx?|pptx?|zip|gz|rar|7z|jpe?g|png|gif|webp|svg|mp[34]|mov|avi|wav|ogg|css|js|json|xml|rss|ics)$/i.test(
    b.pathname,
  );
}

/** An address as it is stored and compared: no fragment, trailing slash kept as written. */
export function normalizePageUrl(raw: string, base?: string): string | null {
  try {
    const u = new URL(raw, base);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    u.hash = '';
    return u.toString();
  } catch {
    return null;
  }
}

// ── robots.txt ───────────────────────────────────────────────────────────────────────────────────────────

export const MONITOR_USER_AGENT = 'HoopoeMonitor';

interface RobotsRule {
  allow: boolean;
  path: string;
}

/**
 * The rules of robots.txt that apply to Hoopoe's monitor: the group naming `HoopoeMonitor`, else the `*` group.
 * Longest matching path wins; `Allow` wins a tie (RFC 9309). `*` and `$` in paths are supported.
 */
export function robotsRules(txt: string): RobotsRule[] {
  const groups: { agents: string[]; rules: RobotsRule[] }[] = [];
  let current: { agents: string[]; rules: RobotsRule[] } | null = null;
  let lastWasAgent = false;
  for (const raw of txt.split(/\r?\n/)) {
    const line = raw.replace(/#.*$/, '').trim();
    const m = /^([A-Za-z-]+)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const field = (m[1] ?? '').toLowerCase();
    const value = (m[2] ?? '').trim();
    if (field === 'user-agent') {
      if (!current || !lastWasAgent) {
        current = { agents: [], rules: [] };
        groups.push(current);
      }
      current.agents.push(value.toLowerCase());
      lastWasAgent = true;
    } else if (field === 'allow' || field === 'disallow') {
      lastWasAgent = false;
      if (!current) continue;
      if (field === 'disallow' && !value) continue;
      current.rules.push({ allow: field === 'allow', path: value });
    } else lastWasAgent = false;
  }
  const mine = groups.filter(g => g.agents.some(a => a !== '*' && MONITOR_USER_AGENT.toLowerCase().includes(a)));
  const chosen = mine.length ? mine : groups.filter(g => g.agents.includes('*'));
  return chosen.flatMap(g => g.rules);
}

function robotsPattern(path: string): RegExp {
  const anchored = path.endsWith('$');
  const body = (anchored ? path.slice(0, -1) : path)
    .split('*')
    .map(s => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');
  return new RegExp(`^${body}${anchored ? '$' : ''}`);
}

export function robotsAllows(rules: RobotsRule[], url: string): boolean {
  let path: string;
  try {
    const u = new URL(url);
    path = u.pathname + u.search;
  } catch {
    return false;
  }
  let best: RobotsRule | null = null;
  for (const r of rules) {
    if (!robotsPattern(r.path).test(path)) continue;
    if (!best || r.path.length > best.path.length || (r.path.length === best.path.length && r.allow)) best = r;
  }
  return best ? best.allow : true;
}

// ── Change detection ─────────────────────────────────────────────────────────────────────────────────────

export interface TextDiffExcerpt {
  added: string[];
  removed: string[];
}
export interface TextDiff {
  added: number;
  removed: number;
  excerpt: TextDiffExcerpt;
}

/** Lines compared for change detection: trimmed, inner whitespace collapsed, empty lines dropped. */
export function textLines(text: string): string[] {
  return text
    .split('\n')
    .map(l => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/**
 * What changed between two versions of a page's text, line by line (as multisets, so reordering alone counts as
 * no change). The excerpt keeps the first few added and removed lines, each shortened.
 */
export function diffText(before: string, after: string, keep = 5, maxChars = 300): TextDiff {
  const count = (lines: string[]) => {
    const m = new Map<string, number>();
    for (const l of lines) m.set(l, (m.get(l) ?? 0) + 1);
    return m;
  };
  const a = textLines(before);
  const b = textLines(after);
  const ca = count(a);
  const cb = count(b);
  const added: string[] = [];
  const removed: string[] = [];
  const seenB = new Map<string, number>();
  for (const l of b) {
    const n = (seenB.get(l) ?? 0) + 1;
    seenB.set(l, n);
    if (n > (ca.get(l) ?? 0)) added.push(l);
  }
  const seenA = new Map<string, number>();
  for (const l of a) {
    const n = (seenA.get(l) ?? 0) + 1;
    seenA.set(l, n);
    if (n > (cb.get(l) ?? 0)) removed.push(l);
  }
  const cut = (l: string) => (l.length > maxChars ? `${l.slice(0, maxChars - 1)}…` : l);
  return {
    added: added.length,
    removed: removed.length,
    excerpt: { added: added.slice(0, keep).map(cut), removed: removed.slice(0, keep).map(cut) },
  };
}

/** The file name a page snapshot is stored under: host and path, then the day, e.g. `example.org-news-2026-09-26.html`. */
export function snapshotFilename(url: string, at = Date.now()): string {
  let base = 'page';
  try {
    const u = new URL(url);
    base = `${u.hostname}${u.pathname}${u.search}`;
    try {
      base = decodeURIComponent(base);
    } catch {}
  } catch {}
  const slug =
    base
      .replace(/[^\p{L}\p{N}.]+/gu, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 120) || 'page';
  return `${slug}-${new Date(at).toISOString().slice(0, 10)}.html`;
}

/** Validates a monitor's settings; returns plain-language problems (empty when fine). */
export function validateScraperInput(input: ScraperInput, creating: boolean, allowLocal = false): string[] {
  const errors: string[] = [];
  if (creating || input.name !== undefined) {
    const name = (input.name ?? '').trim();
    if (!name) errors.push('Give the monitor a name, e.g. the site or page it watches.');
    else if (name.length > 80) errors.push('Keep the name under 80 characters.');
  }
  if (creating || input.url !== undefined) {
    const check = checkMonitorUrl(input.url ?? '', allowLocal);
    if (!check.ok) errors.push(check.message);
  }
  if (input.scope !== undefined && !SCRAPER_SCOPES.includes(input.scope)) errors.push('Choose one page or a section of the site.');
  if (input.schedule !== undefined && !SCRAPER_SCHEDULES.includes(input.schedule)) errors.push('Choose how often to check.');
  if (input.rendering !== undefined && !SCRAPER_RENDERINGS.includes(input.rendering)) errors.push('Choose how pages are read.');
  const { min, max } = SCRAPER_LIMITS.maxPages;
  if (input.maxPages !== undefined && !(Number.isInteger(input.maxPages) && input.maxPages >= min && input.maxPages <= max))
    errors.push(`Pages per check must be between ${min} and ${max}.`);
  for (const [label, v] of [
    ['focus', input.focusSelector],
    ['ignore', input.ignoreSelectors],
  ] as const) {
    if (v == null || v === '') continue;
    if (v.length > SCRAPER_LIMITS.selectorChars)
      errors.push(`Keep the ${label} selector under ${SCRAPER_LIMITS.selectorChars} characters.`);
    else if (/[{}<>;]/.test(v)) errors.push(`The ${label} selector can only hold CSS selectors, such as main or .article-body.`);
  }
  return errors;
}

/** When a monitor next runs after a check at `from`; null for manual monitors. */
export function nextRunAfter(schedule: ScraperSchedule, from = Date.now()): number | null {
  const interval = SCHEDULE_INTERVAL_MS[schedule];
  return interval === null ? null : from + interval;
}
